import { Component, model, OnInit, signal, ChangeDetectorRef } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { Workflow as WorkflowTypes } from "./service/Workflow";
import { WorkflowActionsComponent } from "./actions/workflow.actions.component";

@TypeManagerDecorator("workflow-actions-step")
@Component({
    selector: "workflow-actions-step",
    template: `
        <div class="actions-step-container">
            <div class="step-header">
                <h3>Actions</h3>
                <p class="step-description">
                    Configure actions for each stage. Actions are executed when the stage completes.
                </p>
            </div>

            <div class="add-action-section">
                <div class="add-controls">
                    <select [(ngModel)]="selectedStageForNewAction" class="stage-dropdown">
                        <option value="">Select stage...</option>
                        @for (stage of workflowProcess().stages; track stage.workflowStageId || stage.id) {
                            <option [value]="stage.workflowStageId || stage.id">
                                {{ stage.stageName }}
                            </option>
                        }
                    </select>
                    <button
                        class="aw-button aw-button--outline"
                        [disabled]="!selectedStageForNewAction"
                        (click)="addHttpActionToSelectedStage()"
                    >
                        Add HTTP Action
                    </button>
                    <button
                        class="aw-button aw-button--outline"
                        [disabled]="!selectedStageForNewAction"
                        (click)="addSystemActionToSelectedStage()"
                    >
                        Add System Action
                    </button>
                </div>
            </div>

            <div class="actions-list">
                @for (action of getAllActions(); track action.workflowActionId || action.id) {
                    <div class="action-item">
                        <div class="action-header" (click)="toggleActionExpansion(action)">
                            <div class="action-info">
                                <span class="action-name">{{ getActionDisplayName(action) }}</span>
                                <span class="action-type">{{ getActionTypeDisplay(action) }}</span>
                                <span class="stage-context">in {{ getStageForAction(action)?.stageName }}</span>
                                <span class="action-flags">
                                    @if (action.defaultEnabled !== false) {
                                        <span class="flag enabled">Enabled</span>
                                    }
                                </span>
                            </div>
                            <div class="action-actions">
                                <span class="expand-icon" [class.expanded]="isActionExpanded(action)">
                                    {{ isActionExpanded(action) ? "▼" : "▶" }}
                                </span>
                            </div>
                        </div>

                        @if (isActionExpanded(action)) {
                            <div class="action-content">
                                <div class="single-action-config">
                                    <workflow-actions
                                        [workflowProcess]="workflowProcess()"
                                        [stageId]="
                                            getStageForAction(action)?.workflowStageId || getStageForAction(action)?.id
                                        "
                                        [stage]="getStageForAction(action)"
                                        [actions]="[action]"
                                        (actionsChange)="onActionChange(action, $event)"
                                        [hideAddButtons]="true"
                                        [hideStageHeader]="true"
                                    ></workflow-actions>
                                </div>
                            </div>
                        }
                    </div>
                }
            </div>

            @if (getAllActions().length === 0) {
                <div class="no-actions">
                    <p>
                        No actions defined. Select a stage above and click "Add HTTP Action" or "Add System Action" to
                        create your first action.
                    </p>
                </div>
            }
        </div>
    `,
    styles: `
        .actions-step-container {
            padding: 20px;
        }

        .step-header {
            margin-bottom: 24px;
        }

        .step-header h3 {
            margin: 0 0 8px 0;
            color: #333;
        }

        .step-description {
            margin: 0;
            color: #666;
            font-size: 14px;
        }

        .add-action-section {
            margin-bottom: 24px;
            padding: 16px;
            background: #f8f9fa;
            border: 1px solid #e0e0e0;
            border-radius: 8px;
        }

        .add-controls {
            display: flex;
            gap: 12px;
            align-items: center;
        }

        .stage-dropdown {
            flex: 1;
            padding: 8px 12px;
            border: 1px solid #ccc;
            border-radius: 4px;
            background: white;
        }

        .btn {
            padding: 8px 16px;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;
        }

        .btn-primary {
            background: #007bff;
            color: white;
        }

        .btn-primary:hover:not(:disabled) {
            background: #0056b3;
        }

        .btn-secondary {
            background: #6c757d;
            color: white;
        }

        .btn-secondary:hover:not(:disabled) {
            background: #545b62;
        }

        .btn-primary:disabled,
        .btn-secondary:disabled {
            background: #6c757d;
            cursor: not-allowed;
        }

        .actions-list {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        .action-item {
            background: white;
            border: 1px solid #e0e0e0;
            border-radius: 8px;
            overflow: visible;
            position: relative;
        }

        .action-header {
            padding: 16px;
            background: #f8f9fa;
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            align-items: center;
            transition: background 0.2s;
        }

        .action-header:hover {
            background: #e9ecef;
        }

        .action-info {
            display: flex;
            align-items: center;
            gap: 12px;
        }

        .action-name {
            font-weight: 500;
            color: #333;
        }

        .action-type {
            font-size: 11px;
            color: #666;
            background: #e9ecef;
            padding: 2px 6px;
            border-radius: 8px;
            text-transform: uppercase;
        }

        .stage-context {
            font-size: 12px;
            color: #666;
            font-style: italic;
        }

        .action-flags {
            display: flex;
            gap: 8px;
        }

        .flag {
            padding: 2px 8px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 500;
            text-transform: uppercase;
        }

        .flag.enabled {
            background: #d4edda;
            color: #155724;
        }

        .expand-icon {
            color: #666;
            font-size: 12px;
            transition: transform 0.2s;
        }

        .expand-icon.expanded {
            transform: rotate(90deg);
        }

        .action-content {
            padding: 20px;
            border-top: 1px solid #e0e0e0;
            overflow: visible;
            position: relative;
        }

        .no-actions {
            text-align: center;
            padding: 40px 20px;
            color: #666;
        }
    `,
    standalone: true,
    imports: [CommonModule, FormsModule, WorkflowActionsComponent],
})
export class WorkflowActionsStepComponent implements OnInit {
    workflowProcess = model.required<WorkflowTypes.WorkflowProcess>();

    selectedStageForNewAction: string = "";
    private expandedActions = signal<Set<number | string>>(new Set());

    constructor(private cd: ChangeDetectorRef) {}

    ngOnInit() {}

    getAllActions(): WorkflowTypes.WorkflowAction[] {
        const allActions: WorkflowTypes.WorkflowAction[] = [];
        this.workflowProcess().stages?.forEach((stage) => {
            if (stage.actions && stage.actions.length > 0) {
                allActions.push(...stage.actions);
            }
        });
        return allActions;
    }

    getStageForAction(action: WorkflowTypes.WorkflowAction): WorkflowTypes.WorkflowStage | null {
        return (
            this.workflowProcess().stages?.find((stage) =>
                stage.actions?.some(
                    (a) =>
                        (a.workflowActionId && a.workflowActionId === action.workflowActionId) ||
                        (a.id && a.id === action.id),
                ),
            ) || null
        );
    }

    getActionDisplayName(action: WorkflowTypes.WorkflowAction): string {
        if (action.description && action.description.trim()) {
            return action.description;
        }

        if (action.actionCode) {
            return action.actionCode
                .replace(/_/g, " ")
                .toLowerCase()
                .replace(/\b\w/g, (l) => l.toUpperCase());
        }

        return `${action.actionType} Action`;
    }

    getActionTypeDisplay(action: WorkflowTypes.WorkflowAction): string {
        if (action.actionType === "SYSTEM_ACTION" && action.actionCode) {
            return action.actionCode
                .replace(/_/g, " ")
                .toLowerCase()
                .replace(/\b\w/g, (l) => l.toUpperCase());
        }
        return action.actionType;
    }

    addHttpActionToSelectedStage() {
        if (!this.selectedStageForNewAction) return;

        const stage = this.workflowProcess().stages?.find(
            (s) =>
                (s.workflowStageId && s.workflowStageId.toString() === this.selectedStageForNewAction) ||
                (s.id && s.id.toString() === this.selectedStageForNewAction),
        );

        if (stage) {
            const newAction: WorkflowTypes.WorkflowAction = {
                id: this.getNextNegativeId(),
                workflowProcessId: this.workflowProcess().workflowProcessId,
                workflowStageId: stage.workflowStageId || stage.id,
                actionType: "HTTP_REQUEST",
                description: "HTTP Request Action",
                defaultEnabled: true,
                sortOrder: (stage.actions || []).length + 1,
            };

            if (!stage.actions) {
                stage.actions = [];
            }
            stage.actions.push(newAction);

            this.workflowProcess.set(this.workflowProcess());
            this.cd.detectChanges();
        }
    }

    addSystemActionToSelectedStage() {
        if (!this.selectedStageForNewAction) return;

        const stage = this.workflowProcess().stages?.find(
            (s) =>
                (s.workflowStageId && s.workflowStageId.toString() === this.selectedStageForNewAction) ||
                (s.id && s.id.toString() === this.selectedStageForNewAction),
        );

        if (stage) {
            const newAction: WorkflowTypes.WorkflowAction = {
                id: this.getNextNegativeId(),
                workflowProcessId: this.workflowProcess().workflowProcessId,
                workflowStageId: stage.workflowStageId || stage.id,
                actionType: "SYSTEM_ACTION",
                actionCode: "SEND_EMAIL",
                isSystemAction: true,
                description: "",
                defaultEnabled: true,
                sortOrder: (stage.actions || []).length + 1,
                inputs: [],
            };

            if (!stage.actions) {
                stage.actions = [];
            }
            stage.actions.push(newAction);

            this.workflowProcess.set(this.workflowProcess());
            this.cd.detectChanges();
        }
    }

    private getNextNegativeId(): number {
        const allActions = this.getAllActions();
        if (allActions.length === 0) {
            return -1;
        }
        return allActions.reduce((minId, action) => Math.min(minId, action.id || -1), -1) - 1;
    }

    toggleActionExpansion(action: WorkflowTypes.WorkflowAction) {
        const actionKey = action.workflowActionId || action.id!;
        const expanded = this.expandedActions();

        if (expanded.has(actionKey)) {
            expanded.delete(actionKey);
        } else {
            expanded.add(actionKey);
        }

        this.expandedActions.set(new Set(expanded));
    }

    isActionExpanded(action: WorkflowTypes.WorkflowAction): boolean {
        const actionKey = action.workflowActionId || action.id!;
        return this.expandedActions().has(actionKey);
    }

    onActionChange(originalAction: WorkflowTypes.WorkflowAction, updatedActions: WorkflowTypes.WorkflowAction[]) {
        const stage = this.getStageForAction(originalAction);
        if (stage && stage.actions) {
            if (updatedActions.length === 0) {
                // Action was removed, remove it from the stage
                const actionIndex = stage.actions.findIndex(
                    (a) =>
                        (a.workflowActionId && a.workflowActionId === originalAction.workflowActionId) ||
                        (a.id && a.id === originalAction.id),
                );
                if (actionIndex !== -1) {
                    stage.actions.splice(actionIndex, 1);
                }
            } else {
                // Action was updated, replace it
                const updatedAction = updatedActions[0];
                const actionIndex = stage.actions.findIndex(
                    (a) =>
                        (a.workflowActionId && a.workflowActionId === originalAction.workflowActionId) ||
                        (a.id && a.id === originalAction.id),
                );
                if (actionIndex !== -1) {
                    stage.actions[actionIndex] = updatedAction;
                }
            }
        }
        this.workflowProcess.set(this.workflowProcess());
        this.cd.detectChanges();
    }

    onStageActionsChange(stage: WorkflowTypes.WorkflowStage, actions: WorkflowTypes.WorkflowAction[]) {
        stage.actions = actions;
        this.workflowProcess.set(this.workflowProcess());
        this.cd.detectChanges();
    }
}
